import type { SalvarDespesasExtratoAvaliacaoAnalisePayload } from "../../../services/FinanceiroService";

export type AnaliseDespesaSalvaRow = {
  id?: number;
  idComprovanteDespesa?: number;
  conforme?: boolean | null;
  obsAnalista?: string | null;
  dataSolicitacao?: string | null;
  dataCorrecao?: string | null;
};

export type SalvarConferenciaAnaliseResposta = {
  analisesDespesas?: AnaliseDespesaSalvaRow[];
  obsAnalista?: string | null;
  liberarTotalParcelas?: boolean;
};

type ComprovanteRow = Record<string, unknown>;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") return null;
  return value as Record<string, unknown>;
}

function normalizeAnaliseRow(raw: unknown): AnaliseDespesaSalvaRow | null {
  const row = asRecord(raw);
  if (!row) return null;

  const idComprovanteDespesa = Number(
    row.idComprovanteDespesa ?? row.idComprovante ?? row.despesaExtratoAvaliacaoId,
  );
  if (!Number.isFinite(idComprovanteDespesa) || idComprovanteDespesa <= 0) {
    return null;
  }

  const conformeRaw = row.conforme ?? row.emConformidade;
  const conforme =
    typeof conformeRaw === "boolean" ? conformeRaw : null;

  return {
    id: Number(row.id ?? row.analiseDespesaExtratoAvaliacaoId) || undefined,
    idComprovanteDespesa,
    conforme,
    obsAnalista:
      row.obsAnalista != null
        ? String(row.obsAnalista)
        : row.observacaoAnalista != null
          ? String(row.observacaoAnalista)
          : null,
    dataSolicitacao:
      (row.dataSolicitacao as string | null | undefined) ?? null,
    dataCorrecao: (row.dataCorrecao as string | null | undefined) ?? null,
  };
}

export function normalizeSalvarConferenciaResposta(
  payload: unknown,
): SalvarConferenciaAnaliseResposta {
  const root = asRecord(payload);
  if (!root) return {};

  const nested =
    asRecord(root.data) ??
    asRecord(root.result) ??
    asRecord(root.payload) ??
    root;

  const analisesRaw = nested.analisesDespesas ?? nested.analises;
  const analisesDespesas = Array.isArray(analisesRaw)
    ? analisesRaw
        .map(normalizeAnaliseRow)
        .filter((x): x is AnaliseDespesaSalvaRow => x != null)
    : undefined;

  const obsAnalista =
    nested.obsAnalista != null
      ? String(nested.obsAnalista)
      : nested.observacaoAnalista != null
        ? String(nested.observacaoAnalista)
        : nested.observacaoLiberarValorTotalSolicitacao != null
          ? String(nested.observacaoLiberarValorTotalSolicitacao)
          : undefined;

  const liberarTotalParcelas =
    typeof nested.liberarTotalParcelas === "boolean"
      ? nested.liberarTotalParcelas
      : typeof nested.liberarValorTotalSolicitacao === "boolean"
        ? nested.liberarValorTotalSolicitacao
        : undefined;

  return {
    analisesDespesas,
    obsAnalista,
    liberarTotalParcelas,
  };
}

function coletarAnalisesV2DaResposta(payload: unknown): AnaliseDespesaSalvaRow[] {
  const root = asRecord(payload);
  if (!root) return [];

  const nested =
    asRecord(root.data) ??
    asRecord(root.result) ??
    asRecord(root.payload) ??
    root;

  const despesas = nested.despesas;
  if (!Array.isArray(despesas)) return [];

  const rows: AnaliseDespesaSalvaRow[] = [];

  for (const despesa of despesas) {
    const d = asRecord(despesa);
    if (!d) continue;

    const idComp = Number(d.id ?? d.Id);
    if (!Number.isFinite(idComp) || idComp <= 0) continue;

    const analiseList = d.analise ?? d.Analise;
    const analiseArr = Array.isArray(analiseList) ? analiseList : [];
    const last = analiseArr.length
      ? analiseArr.reduce((acc, item) => {
          const accId = Number(asRecord(acc)?.id ?? 0);
          const itemId = Number(asRecord(item)?.id ?? 0);
          return itemId >= accId ? item : acc;
        })
      : null;

    const a = asRecord(last);
    if (!a) continue;

    rows.push({
      id: Number(a.id) || undefined,
      idComprovanteDespesa: idComp,
      conforme:
        typeof a.emConformidade === "boolean"
          ? a.emConformidade
          : typeof a.EmConformidade === "boolean"
            ? a.EmConformidade
            : null,
      obsAnalista:
        a.observacaoAnalista != null
          ? String(a.observacaoAnalista)
          : a.ObservacaoAnalista != null
            ? String(a.ObservacaoAnalista)
            : null,
    });
  }

  return rows;
}

function analisesFromPayloadV2(
  despesas: SalvarDespesasExtratoAvaliacaoAnalisePayload["despesas"],
): AnaliseDespesaSalvaRow[] {
  return (despesas ?? []).map((d) => ({
    id: Number(d.analiseDespesaExtratoAvaliacaoId) || undefined,
    idComprovanteDespesa: Number(d.despesaExtratoAvaliacaoId),
    conforme:
      typeof d.emConformidade === "boolean" ? d.emConformidade : null,
    obsAnalista:
      d.observacaoAnalista != null ? String(d.observacaoAnalista) : null,
  }));
}

export function mergeComprovantesAposSalvarAnalise(
  comprovantesAtuais: ComprovanteRow[],
  analises: AnaliseDespesaSalvaRow[],
): ComprovanteRow[] {
  if (!analises.length) return comprovantesAtuais ?? [];

  const byComp = new Map(
    analises.map((a) => [Number(a.idComprovanteDespesa), a]),
  );

  return (comprovantesAtuais ?? []).map((c) => {
    const idComp = Number(c.idComprovanteDespesa ?? c.id);
    const analise = byComp.get(idComp);
    if (!analise) return c;

    const obsSalva = String(analise.obsAnalista ?? "").trim();
    const conforme =
      typeof analise.conforme === "boolean" ? analise.conforme : c.conforme;

    const next: ComprovanteRow = {
      ...c,
      obsAnalista: obsSalva,
      obsAnalistaOriginal: obsSalva,
    };

    const novoId = Number(analise.id);
    if (Number.isFinite(novoId) && novoId > 0) {
      next.idAnalise = novoId;
    }

    if (typeof conforme === "boolean") {
      next.conforme = conforme;
      next.status = conforme ? "CONFORME" : "NAO_CONFORME";
    }

    return next;
  });
}

export function mergeComprovantesAposSalvarAnaliseLegacy(
  comprovantesAtuais: ComprovanteRow[],
  resposta: unknown,
  analisesEnviadas: AnaliseDespesaSalvaRow[] = [],
): ComprovanteRow[] {
  const norm = normalizeSalvarConferenciaResposta(resposta);
  const analises =
    norm.analisesDespesas?.length ? norm.analisesDespesas : analisesEnviadas;

  return mergeComprovantesAposSalvarAnalise(comprovantesAtuais, analises);
}

export function mergeComprovantesAposSalvarAnaliseV2(
  comprovantesAtuais: ComprovanteRow[],
  resposta: unknown,
  payloadEnviado: SalvarDespesasExtratoAvaliacaoAnalisePayload,
): ComprovanteRow[] {
  const fromResponse = coletarAnalisesV2DaResposta(resposta);
  const analises = fromResponse.length
    ? fromResponse
    : analisesFromPayloadV2(payloadEnviado.despesas);

  return mergeComprovantesAposSalvarAnalise(comprovantesAtuais, analises);
}

export function extrairMetadadosSalvarConferencia(
  resposta: unknown,
): Pick<SalvarConferenciaAnaliseResposta, "obsAnalista" | "liberarTotalParcelas"> {
  const norm = normalizeSalvarConferenciaResposta(resposta);
  return {
    obsAnalista: norm.obsAnalista,
    liberarTotalParcelas: norm.liberarTotalParcelas,
  };
}
